import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";

const KEY = "spayzo:favorites:v1";
const EVT = "spayzo:favorites-change";

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function write(ids: string[]) {
  localStorage.setItem(KEY, JSON.stringify(ids));
  window.dispatchEvent(new Event(EVT));
}

export function useFavorite(id: string) {
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const sync = () => setSaved(read().includes(id));
    sync();
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [id]);

  const toggle = useCallback(() => {
    const current = read();
    const has = current.includes(id);
    const next = has ? current.filter((x) => x !== id) : [...current, id];
    write(next);
    toast.success(has ? "Removed from favorites" : "Saved to favorites");
  }, [id]);

  return { saved, toggle };
}

export async function shareSpace(payload: { title: string; url?: string }) {
  const url = payload.url ?? (typeof window !== "undefined" ? window.location.href : "");
  const shareData = { title: payload.title, text: payload.title, url };
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share(shareData);
      return;
    }
    await navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  } catch (e: any) {
    if (e?.name === "AbortError") return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to share");
    }
  }
}
